import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import useAdminStore from '../stores/useAdminStore';
import axiosClient from '../services/api';
import useAuthStore from '../stores/useAuthStore';

const AdminSettingsPage = () => {
    const { user } = useAuthStore();
    const { isLoading: storeLoading, error: storeError } = useAdminStore();
    const [activeTab, setActiveTab] = useState('payment');
    const [settings, setSettings] = useState({
        siteName: '',
        siteUrl: '',
        email: {
            from: '',
            smtpHost: '',
            smtpPort: 587,
            smtpUser: '',
            smtpPass: ''
        },
        payment: {
            currency: 'NGN',
            taxRate: 0.08,
            withdrawalThreshold: 5000
        },
        security: {
            passwordMinLength: 8,
            passwordRequireNumbers: true,
            passwordRequireSpecialChars: true,
            sessionTimeout: 3600
        },
        notifications: {
            courseUpdates: true,
            paymentReceipts: true,
            newMessages: true,
            systemAlerts: true,
            weeklyReports: true
        },
        maintenance: {
            enabled: false,
            message: ''
        }
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const [faqs, setFaqs] = useState([]);

    const [editingFaq, setEditingFaq] = useState(null);
    const [faqForm, setFaqForm] = useState({
        question: '',
        answer: '',
        category: 'general'
    });
    const [faqLoading, setFaqLoading] = useState(false);
    const [faqError, setFaqError] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axiosClient.get('/supports/admin/settings');
                setSettings(response.data.settings || {});
            } catch (error) {
                console.error('Failed to fetch settings:', error);
                setSaveError('Failed to load system settings');
            }
        };

        if (activeTab !== 'faq') {
            fetchSettings();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'faq') {
            fetchFaqs();
        }
    }, [activeTab]);

    const fetchFaqs = async () => {
        setFaqLoading(true);
        setFaqError(null);
        try {
            const response = await axiosClient.get('/supports/admin/faq');
            setFaqs(response.data.faqs);
        } catch (error) {
            console.error('Failed to fetch FAQs:', error);
            setFaqError('Failed to load FAQs');
        } finally {
            setFaqLoading(false);
        }
    };

    const handleInputChange = (e, parentKey = null) => {
        const { name, value, type, checked } = e.target;
        const finalValue = type === 'checkbox' ? checked : value;

        if (parentKey) {
            setSettings(prev => ({
                ...prev,
                [parentKey]: {
                    ...prev[parentKey],
                    [name]: finalValue
                }
            }));
        } else {
            setSettings(prev => ({
                ...prev,
                [name]: finalValue
            }));
        }
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            await axiosClient.put('/supports/admin/settings', settings);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to save settings:', error);
            setSaveError(error.response?.data?.error || 'Failed to update settings');
        } finally {
            setIsSaving(false);
        }
    };


    const handleFaqChange = (e) => {
        const { name, value } = e.target;
        setFaqForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateFaq = async (e) => {
        e.preventDefault();
        setFaqLoading(true);
        setFaqError(null);

        try {
            await axiosClient.post('/supports/admin/faq', faqForm);
            await fetchFaqs();
            setFaqForm({ question: '', answer: '', category: 'general' });
        } catch (error) {
            console.error('Failed to create FAQ:', error);
            setFaqError(error.response?.data?.error || 'Failed to create FAQ');
        } finally {
            setFaqLoading(false);
        }
    };

    const handleUpdateFaq = async (e) => {
        e.preventDefault();
        setFaqLoading(true);
        setFaqError(null);

        try {
            await axiosClient.put(`/supports/admin/faq/${editingFaq?._id}`, faqForm);
            await fetchFaqs();
            setEditingFaq(null);
            setFaqForm({ question: '', answer: '', category: 'general' });
        } catch (error) {
            console.error('Failed to update FAQ:', error);
            setFaqError(error.response?.data?.error || 'Failed to update FAQ');
        } finally {
            setFaqLoading(false);
        }
    };

    const handleEditFaq = (faq) => {
        setEditingFaq(faq);
        setFaqForm({
            question: faq.question,
            answer: faq.answer,
            category: faq.category
        });
    };

    const handleDeleteFaq = async (faqId) => {
        if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

        try {
            await axiosClient.delete(`/supports/admin/faq/${faqId}`);
            await fetchFaqs();
        } catch (error) {
            console.error('Failed to delete FAQ:', error);
            setFaqError('Failed to delete FAQ');
        }
    };

    if (storeLoading) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }

    if (storeError) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error</h2>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-4">{storeError}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole={user.role}>
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">System Settings</h1>
                        <p className="text-neutral-600 dark:text-neutral-400">Configure platform-wide settings and manage FAQs</p>
                    </div>

                    {/* Tabs */}
                    <div className="mb-8 border-b border-neutral-200 dark:border-neutral-700">
                        <nav className="-mb-px flex space-x-8">
                            {['payment', 'faq'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                            : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                                        }`}
                                >
                                    {tab === 'faq' ? 'FAQ Management' : `${tab.charAt(0).toUpperCase() + tab.slice(1)} Settings`}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Payment Settings */}
                    {activeTab === 'payment' && (
                        <form onSubmit={handleSaveSettings} className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label htmlFor="currency" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Currency</label>
                                    <select
                                        id="currency"
                                        name="currency"
                                        value={settings.payment?.currency || 'NGN'}
                                        onChange={(e) => handleInputChange(e, 'payment')}
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="NGN">NGN (Naira)</option>
                                        <option value="USD">USD (Dollar)</option>
                                        <option value="EUR">EUR (Euro)</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="taxRate" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Tax Rate (%)</label>
                                    <input
                                        type="number"
                                        id="taxRate"
                                        name="taxRate"
                                        value={settings.payment?.taxRate || 0}
                                        onChange={(e) => handleInputChange(e, 'payment')}
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="withdrawalThreshold" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Withdrawal Threshold (₦)</label>
                                    <input
                                        type="number"
                                        id="withdrawalThreshold"
                                        name="withdrawalThreshold"
                                        value={settings.payment?.withdrawalThreshold || 0}
                                        onChange={(e) => handleInputChange(e, 'payment')}
                                        min="0"
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4">
                                {saveError && <p className="text-sm text-danger-500">{saveError}</p>}
                                {saveSuccess && <p className="text-sm text-success-500">Settings saved successfully!</p>}
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    {isSaving ? 'Saving...' : 'Save Settings'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* FAQ Management */}
                    {activeTab === 'faq' && (
                        <div className="space-y-8">
                            {/* FAQ Form */}
                            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
                                    {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
                                </h2>
                                <form onSubmit={editingFaq ? handleUpdateFaq : handleCreateFaq}>
                                    <div className="grid grid-cols-1 gap-6 mb-6">
                                        <div>
                                            <label htmlFor="question" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Question</label>
                                            <input
                                                type="text"
                                                id="question"
                                                name="question"
                                                value={faqForm.question}
                                                onChange={handleFaqChange}
                                                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="answer" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Answer</label>
                                            <textarea
                                                id="answer"
                                                name="answer"
                                                value={faqForm.answer}
                                                onChange={handleFaqChange}
                                                rows={4}
                                                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="category" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Category</label>
                                            <select
                                                id="category"
                                                name="category"
                                                value={faqForm.category}
                                                onChange={handleFaqChange}
                                                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            >
                                                <option value="general">General</option>
                                                <option value="courses">Courses</option>
                                                <option value="payments">Payments</option>
                                                <option value="technical">Technical</option>
                                                <option value="account">Account</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex justify-end space-x-4">
                                        {faqError && <p className="text-sm text-danger-500">{faqError}</p>}
                                        {editingFaq && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingFaq(null);
                                                    setFaqForm({ question: '', answer: '', category: 'general' });
                                                }}
                                                className="px-4 py-2 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            disabled={faqLoading}
                                            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {faqLoading ? 'Saving...' : editingFaq ? 'Update FAQ' : 'Add FAQ'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* FAQ List */}
                            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Manage FAQs</h2>
                                {faqLoading ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                                    </div>
                                ) : faqs.length > 0 ? (
                                    <div className="space-y-4">
                                        {faqs?.map(faq => (
                                            <div key={faq?._id} className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="font-medium text-neutral-900 dark:text-white">{faq?.question}</h3>
                                                        <p className="mt-2 text-neutral-600 dark:text-neutral-400">{faq?.answer}</p>
                                                        <span className="inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 capitalize">
                                                            {faq?.category}
                                                        </span>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => handleEditFaq(faq)}
                                                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteFaq(faq?._id)}
                                                            className="text-danger-600 hover:text-danger-900 dark:text-danger-400 dark:hover:text-danger-300"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-neutral-500 dark:text-neutral-400 text-center py-8">No FAQs found. Add one above.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AdminSettingsPage;